'use client';

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';

import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to monitoring service
    console.error('Auth Error Boundary caught an error:', error, errorInfo);

    // In production, you might want to send this to a service like Sentry
    // Example: Sentry.captureException(error, { contexts: { errorInfo } });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className='bg-background flex min-h-screen items-center justify-center'>
          <div className='w-full max-w-md space-y-8 text-center'>
            <div className='bg-card rounded-lg px-6 py-8 shadow-md'>
              <AlertTriangle className='mx-auto size-12 text-red-500' />
              <h2 className='text-foreground mt-6 text-2xl font-bold'>
                Something went wrong
              </h2>
              <p className='text-muted-foreground mt-2 text-sm'>
                We encountered an error while processing your authentication.
                Please try again.
              </p>
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className='mt-4 text-left'>
                  <summary className='text-muted-foreground cursor-pointer text-sm'>
                    Error Details (Development)
                  </summary>
                  <pre className='bg-muted mt-2 overflow-auto rounded p-2 text-xs'>
                    {this.state.error.message}
                  </pre>
                </details>
              )}
              <div className='mt-6 space-y-3'>
                <Button
                  onClick={this.handleRetry}
                  className='w-full'>
                  <RefreshCw className='mr-2 size-4' />
                  Try Again
                </Button>
                <Button
                  variant='outline'
                  onClick={() => {
                    window.location.href = '/';
                  }}
                  className='w-full'>
                  Go Home
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook for functional components
export function useAuthErrorHandler() {
  return (error: Error, context?: string) => {
    console.error(`Auth Error${context ? ` in ${context}` : ''}:`, error);

    // In production, send to monitoring
    // Example: Sentry.captureException(error, { tags: { context } });

    // You could also show a toast notification here
    // Example: toast.error("Authentication error occurred");
  };
}
